import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { errorNormalizer } from '../../../shared/Helpers/functions';
import DiscretionarySessionData from './discretionarySessionData';
import SoapNoteButtons from '../components/soapNoteButtons';
import { usePostSubmitEditMode } from '../helpers';
import { ACTIONS, discretionarySessionReducer, initialDiscretionarySessionState } from './discretionarySessionReducer';
import { useResetCalendarData } from '../../Calendar/Helpers/hooks';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getSoapNote, updateDiscretionarySession } from '../../../redux/State/clientSlice/soapNoteSlice';
import { setDate, setEndTime, setStartTime } from '../../../redux/State/clientSlice/sessionSlice';
import {
	DiscretionarySessionProperties,
	UpdateDiscretionarySessionProperties,
} from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';

const DiscretionarySession = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const { resetData } = useResetCalendarData();
	const { isPostSubmitEditMode } = usePostSubmitEditMode();

	const reportId = params.get('reportId');
	const clientId = params.get('clientId');
	const forcedOperation = params.get('forcedOperation') === 'true';

	const sessionInfo = useSelector((state) => state.session.sessionInfo);

	const [data, setData] = useReducer(discretionarySessionReducer, initialDiscretionarySessionState);
	const currentUpdateRequestRef = useRef<ReturnType<typeof dispatch<any>> | null>(null);
	const isMounted = useRef(true);

	useEffect(() => {
		isMounted.current = true;

		return () => {
			isMounted.current = false;

			if (currentUpdateRequestRef.current) {
				console.info('Aborting request on unmount');
				currentUpdateRequestRef.current.abort();
			}
		};
	}, []);

	useEffect(() => {
		if (!reportId || !clientId) return;

		dispatch(getSoapNote({ reportId, clientId }))
			.then(unwrapResult)
			.then((response) => {
				const { date, startTime, endTime } = response as DiscretionarySessionProperties;

				dispatch(setDate(date));
				dispatch(setStartTime(startTime));
				dispatch(setEndTime(endTime));

				setData({
					type: ACTIONS.setState,
					payload: response,
				});
			})
			.catch((error) => {
				errorNormalizer(error);
				if (error.status === 403) navigate('/dashboard');
			});
	}, [dispatch, clientId, reportId, navigate]);

	const updateSoapNoteHandler = useCallback(
		(submit: boolean) => {
			if (currentUpdateRequestRef.current) {
				console.info('Aborting previous pending request');
				currentUpdateRequestRef.current.abort();
			}
			if (!reportId || !clientId || !data) return;
			const { date, startTime, endTime } = sessionInfo;
			const allData: UpdateDiscretionarySessionProperties = {
				...data,
				date,
				startTime,
				endTime,
				reportId,
				submit,
				forcedOperation,
				clientId,
			};
			const requestPromise = dispatch(updateDiscretionarySession(allData));
			currentUpdateRequestRef.current = requestPromise;

			requestPromise
				.then(unwrapResult)
				.then((response) => {
					if (!submit || !isMounted.current) return;
					resetData();

					if (response.status) {
						toast(`Status: ${response.status.name}`);
					}
					toast('Success');
					navigate('/calendar');
				})
				.catch(errorNormalizer)
				.finally(() => {
					if (currentUpdateRequestRef.current === requestPromise) {
						currentUpdateRequestRef.current = null;
					}
				});
		},
		[reportId, clientId, forcedOperation, data, sessionInfo, navigate, resetData, dispatch],
	);

	const saveHandler = useCallback(() => {
		if (isPostSubmitEditMode) return;
		updateSoapNoteHandler(false);
	}, [updateSoapNoteHandler, isPostSubmitEditMode]);
	const submitHandler = useCallback(() => updateSoapNoteHandler(true), [updateSoapNoteHandler]);

	return (
		<>
			<DiscretionarySessionData setData={setData} data={data} save={saveHandler} />
			<SoapNoteButtons save={saveHandler} submit={submitHandler} />
		</>
	);
};

export default DiscretionarySession;
